import { FC, useCallback, useState } from 'react';
import { Button, Form, Input, message, Typography } from 'antd';
import { useAuthDispatchContext } from 'providers/Auth';
import { useNavigate } from 'react-router-dom';
import { LoginForm } from 'providers/Auth/types';

import { LoginContent } from './styled';
import { refreshTokenStorage, tokenStorage } from 'utils/storage';
import { getFirstErrorMessage } from 'utils/showErrorMessage';

const { Title } = Typography;

const Login: FC = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const { signIn, getUserData } = useAuthDispatchContext();
  const [isLoading, setLoading] = useState(false);

  const onFinish = useCallback(
    (values: LoginForm) => {
      setLoading(true);
      signIn(values)
        .then(data => {
          if (data.auth2fa) {
            return navigate('/auth2fa');
          }

          tokenStorage.set(data.accessToken);
          refreshTokenStorage.set(data.refreshToken);

          getUserData();
        })
        .catch(e => {
          if (e?.errors) {
            // 422 field-level validation errors → inline under the fields
            form.setFields(e.errors);
          } else if (e?.response && e.response.status !== 429) {
            // 401 invalid credentials (and other API errors) → surface the
            // BE message. 429 is handled by the blocked modal in the provider.
            message.error(getFirstErrorMessage(e));
          }

          setLoading(false);
        });
    },
    [signIn, getUserData, navigate, form]
  );

  return (
    <LoginContent>
      <Title level={2} style={{ marginBottom: 24 }}>
        Welcome back!
      </Title>
      <Form form={form} layout="vertical" size="large" onFinish={onFinish}>
        <Form.Item
          name="email"
          rules={[{ required: true }, { type: 'email' }]}
          style={{ marginBottom: 16 }}
          messageVariables={{ label: 'Email' }}
        >
          <Input placeholder="Email" />
        </Form.Item>
        <Form.Item
          name="password"
          rules={[{ required: true }]}
          style={{ marginBottom: 8 }}
          messageVariables={{ label: 'Password' }}
        >
          <Input.Password placeholder="Password" />
        </Form.Item>
        <Form.Item>
          <Button type="primary" htmlType="submit" loading={isLoading} block>
            Login
          </Button>
        </Form.Item>
      </Form>
    </LoginContent>
  );
};

export default Login;
